import {
	isArray,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { UserDataEntity } from "./Environment/models";

export interface IRepository<TModel, TId extends string | number> {
	get(id: TId): Promise<TModel | undefined>;
	add(model: TModel): Promise<TId>;
	addOrUpdate(model: TModel): Promise<TId>;
	delete(id: TId): Promise<void>;
}

export class IsolatedUserRepository<TModel, TId extends string | number>
	implements IRepository<TModel, TId> {

	private readonly _database: IDBDatabase;
	private readonly _objectName: string;
	private readonly _userId: number;

	constructor(
		database: IDBDatabase,
		objectName: string,
		userId: number
	) {
		this._database = database;
		this._objectName = objectName;
		this._userId = userId;
	}

	public get(id: TId): Promise<TModel | undefined> {
		const transaction: IDBTransaction = this._database.transaction([this._objectName]);
		const objectStore: IDBObjectStore = transaction.objectStore(this._objectName);
		this.ensureKeyPath(objectStore.keyPath);

		const isolatedId: IDBValidKey = [id, this._userId];

		const request: IDBRequest<TModel | undefined> = objectStore.get(isolatedId);

		return resolveRequest(request);
	}

	public async add(model: TModel): Promise<TId> {
		const transaction: IDBTransaction = this._database.transaction(
			[this._objectName],
			"readwrite"
		);

		const objectStore: IDBObjectStore = transaction.objectStore(this._objectName);
		this.ensureKeyPath(objectStore.keyPath);

		const modelExtendedWithUserId: TModel & UserDataEntity = {
			...model,
			userId: this._userId
		};

		const request: IDBRequest<IDBValidKey> = objectStore.add(modelExtendedWithUserId);

		const dbKey: IDBValidKey = await resolveRequest(request);

		return this.parseDbKey(dbKey);
	}

	public async addOrUpdate(model: TModel): Promise<TId> {
		const transaction: IDBTransaction = this._database.transaction(
			[this._objectName],
			"readwrite"
		);

		const objectStore: IDBObjectStore = transaction.objectStore(this._objectName);
		this.ensureKeyPath(objectStore.keyPath);

		const modelExtendedWithUserId: TModel & UserDataEntity = {
			...model,
			userId: this._userId
		};

		const updateRequest: IDBRequest<IDBValidKey> = objectStore.put(modelExtendedWithUserId);

		const dbKey: IDBValidKey = await resolveRequest(updateRequest);

		return this.parseDbKey(dbKey);
	}

	public async delete(id: TId): Promise<void> {
		const transaction: IDBTransaction = this._database.transaction(
			[this._objectName],
			"readwrite"
		);

		const objectStore: IDBObjectStore = transaction.objectStore(this._objectName);
		this.ensureKeyPath(objectStore.keyPath);

		const isolatedId: IDBValidKey = [id, this._userId];
		const request: IDBRequest<undefined> = objectStore.delete(isolatedId);

		await resolveRequest(request);
	}

	private ensureKeyPath(keyPath: string | string[] | null): void {
		const userIdKey: StringKeyOf<UserDataEntity> = "userId";

		const isValid: boolean = isNotNil(keyPath)
			&& isArray(keyPath)
			&& keyPath.length === 2
			&& keyPath[1] === userIdKey;

		if (!isValid) {
			throw new Error(`Incorrect keyPath configuration for entity ${this._objectName}. KeyPath for isolated repository must end with '${userIdKey}'.`);
		}
	}

	private parseDbKey(id: IDBValidKey): TId {
		if (isArray(id)
			&& id.length === 2
			&& id[1] === this._userId
		) {
			return id[0] as TId;
		}

		throw new Error(`Invalid id received from database '${id}'.`);
	}
}

export class SingleUserRepository<TModel, TId extends string | number>
	implements IRepository<TModel, TId> {

	private readonly _database: IDBDatabase;
	private readonly _objectName: string;
	private readonly _userId: number;

	constructor(
		database: IDBDatabase,
		objectName: string,
		userId: number
	) {
		this._database = database;
		this._objectName = objectName;
		this._userId = userId;
	}

	public async get(id: TId): Promise<TModel | undefined> {
		const transaction: IDBTransaction = this._database.transaction([this._objectName]);
		const objectStore: IDBObjectStore = transaction.objectStore(this._objectName);
		this.ensureSupportedKeyPath(objectStore.keyPath);

		const request: IDBRequest<TModel & UserDataEntity | undefined> = objectStore.get(id);

		const modelWithUserId: TModel & UserDataEntity | undefined = await resolveRequest(request);

		if (isNil(modelWithUserId)) {
			return undefined;
		}

		const {
			userId,
			...result
		} = modelWithUserId;

		if (userId !== this._userId) {
			return undefined;
		}

		return result as TModel;
	}

	public async add(model: TModel): Promise<TId> {
		const transaction: IDBTransaction = this._database.transaction(
			[this._objectName],
			"readwrite"
		);

		const objectStore: IDBObjectStore = transaction.objectStore(this._objectName);
		this.ensureSupportedKeyPath(objectStore.keyPath);

		const modelExtendedWithUserId: TModel & UserDataEntity = {
			...model,
			userId: this._userId
		};

		const keyValue: TId = this.resolveKeyValue(model, objectStore.keyPath);

		const existingItemRequest: IDBRequest<(TModel & UserDataEntity) | undefined> = objectStore
			.get(keyValue);

		const existingItem: (TModel & UserDataEntity) | undefined = await resolveRequest(existingItemRequest);

		if (existingItem && existingItem.userId !== this._userId) {
			const deleteRequest: IDBRequest<undefined> = objectStore.delete(keyValue);

			await resolveRequest(deleteRequest);
		}

		const request: IDBRequest<IDBValidKey> = objectStore.add(modelExtendedWithUserId);

		const dbKey: IDBValidKey = await resolveRequest(request);

		return dbKey as TId;
	}

	public async addOrUpdate(model: TModel): Promise<TId> {
		const transaction: IDBTransaction = this._database.transaction(
			[this._objectName],
			"readwrite"
		);

		const objectStore: IDBObjectStore = transaction.objectStore(this._objectName);
		this.ensureSupportedKeyPath(objectStore.keyPath);

		const modelExtendedWithUserId: TModel & UserDataEntity = {
			...model,
			userId: this._userId
		};

		const updateRequest: IDBRequest<IDBValidKey> = objectStore.put(modelExtendedWithUserId);

		const dbKey: IDBValidKey = await resolveRequest(updateRequest);

		return dbKey as TId;
	}

	public async delete(id: TId): Promise<void> {
		const transaction: IDBTransaction = this._database.transaction([this._objectName], "readwrite");
		const objectStore: IDBObjectStore = transaction.objectStore(this._objectName);
		this.ensureSupportedKeyPath(objectStore.keyPath);

		const request: IDBRequest<undefined> = objectStore.delete(id);

		await resolveRequest(request);
	}

	// Implemented based on the description here
	// https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API/Basic_Terminology#key_path
	private resolveKeyValue(
		model: TModel,
		keyPath: string | readonly string[] | null
	): TId {
		const resolvedKeyPath: string = this.ensureSupportedKeyPath(keyPath);

		const segments: string[] = resolvedKeyPath.split(".");

		let intermediateValue: unknown = model;

		for (const segment of segments) {
			if (isNil(intermediateValue)) {
				throw new Error("Cannot resolve key for a model. Encountered null value.");
			}

			intermediateValue = (intermediateValue as any)[segment];
		}

		if (
			typeof intermediateValue === "string" ||
			typeof intermediateValue === "number"
		) {
			return intermediateValue as TId;
		}

		throw new Error("Cannot resolve key for a model. Supported key value can be either string or number.");
	}

	private ensureSupportedKeyPath(keyPath: string | readonly string[] | null): string {
		const userIdKey: StringKeyOf<UserDataEntity> = "userId";

		if (isNotNil(keyPath)) {
			if (typeof keyPath === "string") {
				return keyPath;
			}

			if (keyPath.length === 1) {
				return keyPath[0];
			}
		}

		throw new Error(`Incorrect keyPath configuration for entity ${this._objectName}. KeyPath for isolated repository must end with '${userIdKey}'.`);
	}
}

async function resolveRequest<TResult>(request: IDBRequest<TResult>): Promise<TResult> {
	return new Promise<TResult>(
		(resolve, reject) => {
			request.onsuccess = () => {
				resolve(request.result);
			};

			request.onerror = () => {
				reject(request.error);
			};
		}
	);
}
